import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Save, Home, Info, Wrench, Phone, RotateCcw } from 'lucide-react';
import { siteAPI } from '../lib/api';
import { useToast } from '../hooks/use-toast';

/**
 * Small reusable input + textarea helpers with matching admin dark style.
 */
const inputCls = 'bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E]';
const textareaCls = inputCls + ' min-h-[90px]';

const BilingualField = ({ label, value, onChange, textarea = false, testid }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <div>
      <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">{label} · Shqip 🇦🇱</label>
      {textarea ? (
        <Textarea
          className={textareaCls}
          value={value?.sq || ''}
          onChange={(e) => onChange({ ...(value || {}), sq: e.target.value })}
          data-testid={testid ? `${testid}-sq` : undefined}
        />
      ) : (
        <Input
          className={inputCls}
          value={value?.sq || ''}
          onChange={(e) => onChange({ ...(value || {}), sq: e.target.value })}
          data-testid={testid ? `${testid}-sq` : undefined}
        />
      )}
    </div>
    <div>
      <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">{label} · English 🇬🇧</label>
      {textarea ? (
        <Textarea
          className={textareaCls}
          value={value?.en || ''}
          onChange={(e) => onChange({ ...(value || {}), en: e.target.value })}
          data-testid={testid ? `${testid}-en` : undefined}
        />
      ) : (
        <Input
          className={inputCls}
          value={value?.en || ''}
          onChange={(e) => onChange({ ...(value || {}), en: e.target.value })}
          data-testid={testid ? `${testid}-en` : undefined}
        />
      )}
    </div>
  </div>
);

const PlainField = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">{label}</label>
    <Input
      className={inputCls}
      value={value || ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  </div>
);

export const SiteContentEditor = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ hero: {}, about: {}, services: {}, contact: {} });
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const d = await siteAPI.get();
      setData({
        hero: d.hero || {},
        about: d.about || {},
        services: d.services || {},
        contact: d.contact || {},
      });
    } catch (err) {
      toast({ title: 'Gabim', description: 'Nuk u ngarkua përmbajtja', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await siteAPI.update(data);
      toast({ title: '✓ Ruajtur', description: 'Ndryshimet u ruajtën me sukses. Rifresko faqen kryesore për t\'i parë.' });
    } catch (err) {
      toast({
        title: 'Gabim',
        description: err.response?.data?.detail || 'Ruajtja dështoi',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const set = (section, key, val) => {
    setData(prev => ({ ...prev, [section]: { ...prev[section], [key]: val } }));
  };

  const sections = [
    { id: 'hero', label: 'Ballina', icon: Home },
    { id: 'about', label: 'Rreth Nesh', icon: Info },
    { id: 'services', label: 'Shërbimet', icon: Wrench },
    { id: 'contact', label: 'Kontakti', icon: Phone },
  ];

  if (loading) {
    return (
      <div className="text-center py-16 text-gray-400">
        <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-[#C5A05E]" />
        Duke ngarkuar përmbajtjen...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="site-content-editor">
      <Card className="backdrop-blur-xl bg-white/5 border border-white/10">
        <CardHeader className="border-b border-white/10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl font-bold text-white">Menaxho Përmbajtjen e Faqes</CardTitle>
              <p className="text-sm text-gray-400 mt-1">Ndrysho tekstet e Ballinës, Rreth Nesh, Shërbimet dhe Kontaktit. Ndryshimet reflektohen menjëherë.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={load} className="backdrop-blur-xl bg-white/5 border border-white/20 text-white hover:bg-white/10" data-testid="site-content-reload">
                <RotateCcw className="w-4 h-4 mr-2" />
                Ringarko
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] text-white font-bold"
                data-testid="site-content-save"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Duke ruajtur...</> : <><Save className="w-4 h-4 mr-2" />Ruaj Ndryshimet</>}
              </Button>
            </div>
          </div>
          {/* Section selector pills */}
          <div className="flex flex-wrap gap-2 mt-6">
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                data-testid={`site-content-section-${s.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeSection === s.id
                    ? 'bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] text-white shadow-lg shadow-[#C5A05E]/30'
                    : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10'
                }`}
              >
                <s.icon className="w-4 h-4" />
                {s.label}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {activeSection === 'hero' && (
            <div className="space-y-6">
              <BilingualField label="Titulli Kryesor" value={data.hero.title} onChange={(v) => set('hero', 'title', v)} testid="hero-title" />
              <BilingualField label="Nëntitulli" value={data.hero.subtitle} onChange={(v) => set('hero', 'subtitle', v)} textarea testid="hero-subtitle" />
              <BilingualField label="Butoni Kryesor" value={data.hero.cta} onChange={(v) => set('hero', 'cta', v)} testid="hero-cta" />
              <BilingualField label="Butoni Dytësor" value={data.hero.ctaSecondary} onChange={(v) => set('hero', 'ctaSecondary', v)} testid="hero-cta2" />
              <BilingualField label="Etiketa (badge)" value={data.hero.badge} onChange={(v) => set('hero', 'badge', v)} testid="hero-badge" />

              <div className="border-t border-white/10 pt-4">
                <h4 className="text-white font-bold mb-4">🖼️ Imazhet</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PlainField label="Imazhi Sfond (URL)" value={data.hero.background_image} onChange={(v) => set('hero', 'background_image', v)} placeholder="https://..." testid="hero-bg-img" />
                  <PlainField label="Imazhi i Pronës Kryesore (URL)" value={data.hero.featured_image} onChange={(v) => set('hero', 'featured_image', v)} placeholder="https://..." testid="hero-feat-img" />
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <h4 className="text-white font-bold mb-4">🏠 Karta e Pronës Kryesore</h4>
                <BilingualField label="Etiketa e Kartës" value={data.hero.featured_badge} onChange={(v) => set('hero', 'featured_badge', v)} />
                <BilingualField label="Titulli i Pronës" value={data.hero.featured_title} onChange={(v) => set('hero', 'featured_title', v)} />
                <BilingualField label="Vendndodhja" value={data.hero.featured_location} onChange={(v) => set('hero', 'featured_location', v)} />
                <div className="grid grid-cols-3 gap-4">
                  <PlainField label="Dhoma" value={data.hero.featured_bedrooms} onChange={(v) => set('hero', 'featured_bedrooms', v)} />
                  <PlainField label="Banjo" value={data.hero.featured_bathrooms} onChange={(v) => set('hero', 'featured_bathrooms', v)} />
                  <PlainField label="Sipërfaqe (m²)" value={data.hero.featured_area} onChange={(v) => set('hero', 'featured_area', v)} />
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <h4 className="text-white font-bold mb-4">📊 Statistikat (shfaqen edhe te Rreth Nesh)</h4>
                <div className="space-y-4">
                  {[1, 2, 3].map(n => (
                    <div key={n} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <PlainField label={`Statistika ${n} · Vlera`} value={data.hero[`stat${n}_value`]} onChange={(v) => set('hero', `stat${n}_value`, v)} placeholder="p.sh. 20+" />
                      <div className="md:col-span-2">
                        <BilingualField label={`Statistika ${n} · Etiketa`} value={data.hero[`stat${n}_label`]} onChange={(v) => set('hero', `stat${n}_label`, v)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'about' && (
            <div className="space-y-6">
              <BilingualField label="Etiketa e Seksionit" value={data.about.title} onChange={(v) => set('about', 'title', v)} testid="about-title" />
              <BilingualField label="Titulli i Madh" value={data.about.specialization} onChange={(v) => set('about', 'specialization', v)} testid="about-specialization" />
              <BilingualField label="Përshkrimi" value={data.about.description} onChange={(v) => set('about', 'description', v)} textarea testid="about-description" />

              <div className="border-t border-white/10 pt-4">
                <h4 className="text-white font-bold mb-4">🎯 Kartat e Specializimit (4)</h4>
                <div className="space-y-4">
                  <BilingualField label="Karta 1" value={data.about.card1_title} onChange={(v) => set('about', 'card1_title', v)} />
                  <BilingualField label="Karta 2" value={data.about.card2_title} onChange={(v) => set('about', 'card2_title', v)} />
                  <BilingualField label="Karta 3" value={data.about.card3_title} onChange={(v) => set('about', 'card3_title', v)} />
                  <BilingualField label="Karta 4" value={data.about.card4_title} onChange={(v) => set('about', 'card4_title', v)} />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'services' && (
            <div className="space-y-6">
              <BilingualField label="Etiketa e Seksionit" value={data.services.title} onChange={(v) => set('services', 'title', v)} testid="services-title" />
              <BilingualField label="Nëntitulli" value={data.services.subtitle} onChange={(v) => set('services', 'subtitle', v)} textarea testid="services-subtitle" />

              {[
                { key: 'sales', label: 'Shitje & Blerje' },
                { key: 'rental', label: 'Menaxhim Qiraje' },
                { key: 'investment', label: 'Këshillim Investimi' },
                { key: 'property', label: 'Menaxhim Prone' },
              ].map(s => (
                <div key={s.key} className="border-t border-white/10 pt-4">
                  <h4 className="text-white font-bold mb-4">🔧 {s.label}</h4>
                  <BilingualField label="Titulli" value={data.services[`${s.key}_title`]} onChange={(v) => set('services', `${s.key}_title`, v)} />
                  <BilingualField label="Përshkrimi" value={data.services[`${s.key}_description`]} onChange={(v) => set('services', `${s.key}_description`, v)} textarea />
                </div>
              ))}
            </div>
          )}

          {activeSection === 'contact' && (
            <div className="space-y-6">
              <BilingualField label="Etiketa e Seksionit" value={data.contact.title} onChange={(v) => set('contact', 'title', v)} testid="contact-title" />
              <BilingualField label="Nëntitulli" value={data.contact.subtitle} onChange={(v) => set('contact', 'subtitle', v)} textarea testid="contact-subtitle" />
              <BilingualField label="Titulli i Kartës së Info" value={data.contact.info_title} onChange={(v) => set('contact', 'info_title', v)} />
              <BilingualField label="Adresa" value={data.contact.address} onChange={(v) => set('contact', 'address', v)} />

              <div className="border-t border-white/10 pt-4">
                <h4 className="text-white font-bold mb-4">📞 Të Dhënat e Kontaktit</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PlainField label="Email" value={data.contact.email} onChange={(v) => set('contact', 'email', v)} testid="contact-email" placeholder="info@kaya-group.eu" />
                  <PlainField label="Telefon Kosovë" value={data.contact.phone_kosovo} onChange={(v) => set('contact', 'phone_kosovo', v)} testid="contact-phone-ks" placeholder="+383 48 781 888" />
                  <PlainField label="Telefon Shqipëri" value={data.contact.phone_albania} onChange={(v) => set('contact', 'phone_albania', v)} testid="contact-phone-al" placeholder="+355 67 200 0026" />
                  <PlainField label="WhatsApp — për shfaqje" value={data.contact.whatsapp_display} onChange={(v) => set('contact', 'whatsapp_display', v)} testid="contact-wa-display" placeholder="+355 67 200 0026" />
                  <PlainField
                    label="WhatsApp — numri (vetëm shifra, për link)"
                    value={data.contact.whatsapp_number}
                    onChange={(v) => set('contact', 'whatsapp_number', v)}
                    testid="contact-wa-number"
                    placeholder="355672000026"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
